import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { colors, radii, typography, fontWeights, spacing } from '../../styles/designTokens';

// ── Styles ──────────────────────────────────────────────────

const containerStyle = {
  backgroundColor: colors.urgentBackground,
  border: `1px solid ${colors.urgent}`,
  borderRadius: radii.inner,
  padding: spacing.cardPadding,
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
};

const headerStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  color: colors.urgent,
  fontSize: typography.subheading.fontSize,
  fontWeight: fontWeights.semibold,
};

const messageStyle = {
  fontSize: typography.body.fontSize,
  fontWeight: fontWeights.regular,
  color: colors.textPrimary,
  margin: 0,
};

const actionsStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginTop: '4px',
};

const confirmButtonStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '4px',
  padding: '8px 16px',
  borderRadius: radii.button,
  fontSize: typography.body.fontSize,
  fontWeight: fontWeights.medium,
  border: 'none',
  cursor: 'pointer',
  backgroundColor: colors.urgent,
  color: '#ffffff',
  minWidth: '44px',
  minHeight: '44px',
  transition: 'all 0.15s',
};

const confirmButtonDisabledStyle = {
  ...confirmButtonStyle,
  opacity: 0.6,
  cursor: 'not-allowed',
};

const cancelButtonStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '8px 16px',
  borderRadius: radii.button,
  fontSize: typography.body.fontSize,
  fontWeight: fontWeights.medium,
  border: `1px solid ${colors.divider}`,
  cursor: 'pointer',
  backgroundColor: colors.surface,
  color: colors.textPrimary,
  minWidth: '44px',
  minHeight: '44px',
  transition: 'all 0.15s',
};

const errorStyle = {
  fontSize: typography.caption.fontSize,
  fontWeight: fontWeights.medium,
  color: colors.urgent,
  margin: 0,
};

/**
 * DeleteConfirmation — Inline confirmation panel for trip deletion.
 *
 * Props:
 * @param {string} tripName — Name of the trip to delete (displayed in prompt)
 * @param {boolean} deleting — Whether a deletion request is currently in progress
 * @param {string|null} error — Error message to display after a failed deletion
 * @param {() => void} onConfirm — Called when the traveler confirms deletion
 * @param {() => void} onCancel — Called when the traveler cancels deletion
 */
function DeleteConfirmation({ tripName, deleting, error, onConfirm, onCancel }) {
  return (
    <div style={containerStyle} data-testid="delete-confirmation" role="alertdialog" aria-labelledby="delete-confirm-title">
      <div style={headerStyle} id="delete-confirm-title">
        <AlertTriangle size={18} />
        <span>Delete Trip</span>
      </div>

      <p style={messageStyle}>
        Delete &apos;{tripName}&apos;?
      </p>

      {error && (
        <p style={errorStyle} data-testid="delete-error" role="alert">
          {error}
        </p>
      )}

      <div style={actionsStyle}>
        <button
          style={deleting ? confirmButtonDisabledStyle : confirmButtonStyle}
          onClick={onConfirm}
          disabled={deleting}
          data-testid="delete-confirm"
          aria-busy={deleting}
        >
          {deleting ? 'Deleting\u2026' : 'Confirm'}
        </button>

        <button
          style={cancelButtonStyle}
          onClick={onCancel}
          data-testid="delete-cancel"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

export default DeleteConfirmation;
